/**
 * useModerationSession hook
 *
 * Manages the multi-card moderation session state using useReducer.
 * Handles state transitions: FOCUS_CASE, APPLY_LABELS, UNDO_LABELS,
 * SUBMIT_CASES, LOAD_NEXT_CASES, STOP_SESSION.
 */

import { useCallback, useMemo, useReducer } from 'react';

import type { ModerationCase } from '../domain/ModerationCase';
import type {
  ApplyLabelsAction,
  CaseCardState,
  FocusCaseAction,
  LoadNextCasesAction,
  ModerationSessionAction,
  ModerationSessionState,
  SubmitCasesAction,
  UndoLabelsAction,
} from '../domain/ModerationSession';

/**
 * Creates initial card states from an array of cases
 */
function createCardStates(cases: ModerationCase[]): Record<string, CaseCardState> {
  const cardStates: Record<string, CaseCardState> = {};

  for (const moderationCase of cases) {
    cardStates[moderationCase.caseId] = {
      caseId: moderationCase.caseId,
      status: 'pending',
      appliedLabels: [],
      comment: undefined,
    };
  }

  return cardStates;
}

/**
 * Creates the initial session state
 */
function createInitialState(cases: ModerationCase[] = []): ModerationSessionState {
  return {
    cases,
    cardStates: createCardStates(cases),
    focusedCaseId: cases.length > 0 ? cases[0].caseId : null,
    completedCount: 0,
    isActive: true,
  };
}

/** Handle FOCUS_CASE — sets the focused card if it exists in the session */
function handleFocusCase(state: ModerationSessionState, payload: FocusCaseAction['payload']): ModerationSessionState {
  // Bail out early if already focused — avoids creating a new state reference
  if (state.focusedCaseId === payload.caseId) {
    return state;
  }

  const caseExists = state.cases.some(c => c.caseId === payload.caseId);

  if (!caseExists) {
    return state;
  }

  return { ...state, focusedCaseId: payload.caseId };
}

/** Handle APPLY_LABELS — marks a card as labels-applied with the given labels */
function handleApplyLabels(state: ModerationSessionState, payload: ApplyLabelsAction['payload']): ModerationSessionState {
  const existingCardState = state.cardStates[payload.caseId];

  if (!existingCardState) {
    return state;
  }

  return {
    ...state,
    cardStates: {
      ...state.cardStates,
      [payload.caseId]: {
        ...existingCardState,
        status: 'labels-applied',
        appliedLabels: payload.labels,
        comment: payload.comment,
      },
    },
  };
}

/** Handle UNDO_LABELS — reverts a card back to pending status */
function handleUndoLabels(state: ModerationSessionState, payload: UndoLabelsAction['payload']): ModerationSessionState {
  const existingCardState = state.cardStates[payload.caseId];

  if (!existingCardState) {
    return state;
  }

  return {
    ...state,
    cardStates: {
      ...state.cardStates,
      [payload.caseId]: {
        ...existingCardState,
        status: 'pending',
        appliedLabels: [],
        comment: undefined,
      },
    },
  };
}

/** Handle SUBMIT_CASES — removes submitted cases and updates completed count */
function handleSubmitCases(state: ModerationSessionState, payload: SubmitCasesAction['payload']): ModerationSessionState {
  const submittedSet = new Set(payload.submittedCaseIds);
  const remainingCases = state.cases.filter(c => !submittedSet.has(c.caseId));
  const remainingCardStates: Record<string, CaseCardState> = {};

  for (const c of remainingCases) {
    if (state.cardStates[c.caseId]) {
      remainingCardStates[c.caseId] = state.cardStates[c.caseId];
    }
  }

  const focusedCaseId =
    state.focusedCaseId && submittedSet.has(state.focusedCaseId) ? (remainingCases.length > 0 ? remainingCases[0].caseId : null) : state.focusedCaseId;

  return {
    ...state,
    cases: remainingCases,
    cardStates: remainingCardStates,
    focusedCaseId,
    completedCount: state.completedCount + payload.submittedCaseIds.length,
  };
}

/** Handle LOAD_NEXT_CASES — appends new cases and sets focus if needed */
function handleLoadNextCases(state: ModerationSessionState, payload: LoadNextCasesAction['payload']): ModerationSessionState {
  const newCardStates = createCardStates(payload.cases);
  const allCases = [...state.cases, ...payload.cases];
  const allCardStates = { ...state.cardStates, ...newCardStates };
  const focusedCaseId = state.focusedCaseId ?? (allCases.length > 0 ? allCases[0].caseId : null);

  return { ...state, cases: allCases, cardStates: allCardStates, focusedCaseId };
}

/** Handle STOP_SESSION — marks session as inactive */
function handleStopSession(state: ModerationSessionState): ModerationSessionState {
  return { ...state, isActive: false };
}

/**
 * Reducer for moderation session state transitions.
 * Delegates to per-action handler functions to keep complexity within limits.
 */
function moderationSessionReducer(state: ModerationSessionState, action: ModerationSessionAction): ModerationSessionState {
  switch (action.type) {
    case 'FOCUS_CASE':
      return handleFocusCase(state, action.payload);
    case 'APPLY_LABELS':
      return handleApplyLabels(state, action.payload);
    case 'UNDO_LABELS':
      return handleUndoLabels(state, action.payload);
    case 'SUBMIT_CASES':
      return handleSubmitCases(state, action.payload);
    case 'LOAD_NEXT_CASES':
      return handleLoadNextCases(state, action.payload);
    case 'STOP_SESSION':
      return handleStopSession(state);
    default:
      return state;
  }
}

/** Return type for useModerationSession hook */
export interface UseModerationSessionReturn {
  /** Current session state */
  state: ModerationSessionState;
  /** Dispatch an action to update session state */
  dispatch: React.Dispatch<ModerationSessionAction>;
  /** Focus a specific case card */
  focusCase: (caseId: string) => void;
  /** Apply labels to a case */
  applyLabels: (caseId: string, labels: string[], comment?: string) => void;
  /** Undo applied labels on a case */
  undoLabels: (caseId: string) => void;
  /** Submit cases that have labels applied */
  submitCases: (submittedCaseIds: string[]) => void;
  /** Load next batch of cases */
  loadNextCases: (cases: ModerationCase[]) => void;
  /** Stop the moderation session */
  stopSession: () => void;
  /** Count of cases with labels applied (ready to submit) */
  appliedCount: number;
  /** Case IDs that have labels applied */
  appliedCaseIds: string[];
  /** Non-submitted case IDs (for release on stop) */
  nonSubmittedCaseIds: string[];
  /** Whether any cases have labels applied */
  hasAppliedCases: boolean;
}

/**
 * Hook to manage the multi-card moderation session state
 *
 * @param initialCases - Initial cases to populate the session
 * @returns Session state and action dispatchers
 */
export function useModerationSession(initialCases: ModerationCase[] = []): UseModerationSessionReturn {
  const [state, dispatch] = useReducer(moderationSessionReducer, initialCases, createInitialState);

  const focusCase = useCallback((caseId: string) => dispatch({ type: 'FOCUS_CASE', payload: { caseId } }), []);

  const applyLabels = useCallback(
    (caseId: string, labels: string[], comment?: string) => dispatch({ type: 'APPLY_LABELS', payload: { caseId, labels, comment } }),
    []
  );

  const undoLabels = useCallback((caseId: string) => dispatch({ type: 'UNDO_LABELS', payload: { caseId } }), []);

  const submitCases = useCallback((submittedCaseIds: string[]) => dispatch({ type: 'SUBMIT_CASES', payload: { submittedCaseIds } }), []);

  const loadNextCases = useCallback((cases: ModerationCase[]) => dispatch({ type: 'LOAD_NEXT_CASES', payload: { cases } }), []);

  const stopSession = useCallback(() => dispatch({ type: 'STOP_SESSION' }), []);

  /** Derived: case IDs with labels-applied status */
  const appliedCaseIds = useMemo(
    () =>
      Object.values(state.cardStates)
        .filter(cs => cs.status === 'labels-applied')
        .map(cs => cs.caseId),
    [state.cardStates]
  );

  /** Derived: count of cases ready to submit */
  const appliedCount = appliedCaseIds.length;

  /** Derived: whether any cases have labels applied */
  const hasAppliedCases = appliedCount > 0;

  /** Derived: all case IDs not yet submitted (for release) */
  const nonSubmittedCaseIds = useMemo(() => state.cases.map(c => c.caseId), [state.cases]);

  return {
    state,
    dispatch,
    focusCase,
    applyLabels,
    undoLabels,
    submitCases,
    loadNextCases,
    stopSession,
    appliedCount,
    appliedCaseIds,
    nonSubmittedCaseIds,
    hasAppliedCases,
  };
}
